/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { gnoment } from 'shared-ui/gnoment';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import memoize from 'lodash/memoize';
import sortBy from 'lodash/sortBy';
import { SecurityHelper } from 'sarsaparilla';
import download from 'downloadjs';
import { printBlob } from 'ui-permit/src/utils/utilsNew';
import { PRICE_MAGNITUDE } from '../constants';
import { expandBack, expandForward, findPricingPeriod } from './date_time_utils';
import { apiHelpers } from './api-helpers';

/**
 * NOTE: This file is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */

/**
 * Takes a gatewayId (also sometimes called assetId) and returns the correct path to the gateway page based on the tenant config
 * Adding this temporarily since widget does not support typescript yet.
 * We could use this one instead import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl'; when we add support for typescript in the widget
 */
export function getGatewayUrl(gatewayId, baseDomain) {
    if (!gatewayId) return undefined;

    let path = `/camping/gateways/${gatewayId}`;

    if (baseDomain) {
        path = `${baseDomain}${path}`;
    }

    return path;
}

export function memoizeDebounce(func, wait = 0, options = {}) {
    const mem = memoize(() => debounce(func, wait, options), options.resolver);
    return (...args) => {
        mem.apply(this, args).apply(this, args);
    };
}

export function capitalize(str = '') {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

export const getDivision = (periods = [], startDate, allowPast = false) => {
    const sortedPeriods = sortBy(periods, 'start_date');

    const lowerbound = gnoment(startDate).isBefore() ? gnoment().format() : startDate;
    const activePeriods = allowPast
        ? sortedPeriods
        : sortedPeriods.filter(
              (x) => gnoment(x.start_date).format('YYYY') >= gnoment().format('YYYY')
          );

    let division = get(
        activePeriods.find(
            ({ start_date }) => start_date <= gnoment(lowerbound).format()
        ),
        'divisions',
        []
    ).find(({ starts_at, ends_at }) => {
        // if arrived here, start_date year is current year
        const starts_at_new = `${gnoment().year()}-${starts_at}`;
        const ends_at_new = `${gnoment().year()}-${ends_at}`;

        return (
            gnoment(starts_at_new, 'YYYY-MM-DD').isSameOrBefore(gnoment(lowerbound)) &&
            gnoment(ends_at_new, 'YYYY-MM-DD')
                .add(1, 'day')
                .isSameOrAfter(gnoment(lowerbound))
        );
    });

    // Show future pricing if available.
    if (!division && get(sortedPeriods, '[0].divisions.length')) {
        division = sortedPeriods[0].divisions[0];
    }

    return division;
};

export const formatPrice = (value = 0) => `$${(value / PRICE_MAGNITUDE).toFixed(2)}`;

export const getPrice = (periods, startDate, allowPast = false) =>
    get(getDivision(periods, startDate, allowPast), 'display_price', 0) ||
    get(getDivision(periods, '2010-01-01', true), 'display_price', 0) ||
    formatPrice(
        get(getDivision(periods, startDate, allowPast), 'price', 0) ||
            get(getDivision(periods, '2010-01-01', true), 'price', 0)
    );

export const getSeasonDates = (periods, startDate) => {
    const period = findPricingPeriod(
        periods,
        startDate || gnoment().startOf('day').format()
    );
    if (!period) {
        return ['', ''];
    }

    const startMoment = gnoment(startDate || undefined);
    const startMonthDay = startMoment.format('MM-DD');

    const year = startMoment.year();
    let closest = null;
    let first = null;

    for (const div of period.divisions) {
        if (startMonthDay >= div.starts_at && startMonthDay <= div.ends_at) {
            if (div.starts_at === '01-01') {
                const [newStart, expanded] = expandBack(period.divisions, div);
                let startYear = year;
                if (expanded) {
                    startYear = year - 1;
                }
                return [`${startYear}-${newStart.starts_at}`, `${year}-${div.ends_at}`];
            }
            if (div.ends_at === '12-31') {
                const [newEnd, expanded] = expandForward(period.divisions, div);
                let endYear = year;
                if (expanded) {
                    endYear = year + 1;
                }
                return [`${year}-${div.starts_at}`, `${endYear}-${newEnd.ends_at}`];
            }
            return [`${year}-${div.starts_at}`, `${year}-${div.ends_at}`];
        }

        if (
            startMonthDay <= div.starts_at &&
            (closest === null || div.starts_at < closest.starts_at)
        ) {
            closest = div;
        }
        if (first === null || first.starts_at > div.starts_at) {
            first = div;
        }
    }
    if (closest !== null) {
        const [newStart, expanded] = expandBack(period.divisions, closest, startMonthDay);
        let startYear = year;
        let endYear = year;
        if (expanded) {
            startYear = year - 1;
        }
        const [newEnd, expandedForward] = expandForward(
            period.divisions,
            closest,
            startMonthDay
        );
        if (expandedForward) {
            endYear = year + 1;
        }

        return [`${startYear}-${newStart.starts_at}`, `${endYear}-${newEnd.ends_at}`];
    }
    if (first !== null) {
        const [newStart, expanded] = expandBack(period.divisions, first, startMonthDay);
        let startYear = year;
        let endYear = year;
        if (expanded) {
            startYear = year - 1;
        }
        const [newEnd, expandedForward] = expandForward(
            period.divisions,
            first,
            startMonthDay
        );
        if (expandedForward) {
            endYear = year + 1;
        }

        return [`${startYear}-${newStart.starts_at}`, `${endYear}-${newEnd.ends_at}`];
    }

    return ['', ''];
};

const printPDFUrl = (pdfUrl) => {
    if (!pdfUrl) return;
    axios
        .get(pdfUrl.replace(/\/pdf\b/, '/print'), {
            headers: { Authorization: SecurityHelper.getAuthHeader() },
            responseType: 'arraybuffer',
        })
        .then((response) =>
            printBlob(new Blob([response.data], { type: 'application/pdf' }))
        );
};

export const printPass = ({ downloadUrl, reservationId }) => {
    if (!downloadUrl && !reservationId) return;

    if (downloadUrl) {
        printPDFUrl(downloadUrl);
    } else if (process.env.API) {
        axios
            .get(`${process.env.API}/parkpass/passes/my-passes`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data: { passes } }) => {
                const pass = passes.find(({ id }) => id === reservationId);
                if (pass) {
                    printPDFUrl(pass.download_url);
                }
            });
    }
};

const { getWithAuth } = apiHelpers(SecurityHelper);

export const downloadPassPdf = (downloadUrl, location) =>
    getWithAuth(downloadUrl, { responseType: 'arraybuffer' })
        .then((response) => {
            const filename = location.replace(/\s/g, '-');
            const content = response.headers['content-type'];
            download(response.data, `${filename}.pdf`, content);
        })
        /* eslint-disable-next-line no-console */
        .catch((error) => console.log(error));

export const getUpsellDates = (reservation) => {
    const formatDate = (date) => gnoment.utc(date).format();
    const invType = reservation.inventory_type;
    switch (invType) {
        case 'ACTIVITYPASS':
            return {
                start_date: formatDate(get(reservation, 'order_details.valid_from')),
                end_date: formatDate(get(reservation, 'order_details.valid_to')),
            };
        case 'CAMPING':
            return {
                start_date: formatDate(get(reservation, 'order_details.check_in')),
                end_date: formatDate(get(reservation, 'order_details.check_out')),
            };
        case 'PERMIT':
            return {
                start_date: formatDate(get(reservation, 'order_details.entry_date')),
                end_date: formatDate(get(reservation, 'order_details.exit_date')),
            };
        case 'TICKET':
            return {
                start_date: formatDate(get(reservation, 'order_details.tour_date')),
                end_date: formatDate(get(reservation, 'order_details.tour_date')),
            };
        case 'TIMED_ENTRY':
            return {
                start_date: formatDate(get(reservation, 'order_details.tour_date')),
                end_date: formatDate(get(reservation, 'order_details.tour_date')),
            };
        case 'VEHICLE_PERMIT':
            return {
                start_date: formatDate(get(reservation, 'order_details.start_time')),
                end_date: formatDate(get(reservation, 'order_details.end_time')),
            };
        default:
            return { start_date: null, end_date: null };
    }
};

export const googlePassURL = (passID) => {
    if (process.env.API) {
        return `${process.env.API}/parkpass/passes/${passID}/googlepass`;
    }
    return `/api/parkpass/passes/${passID}/googlepass`;
};

export const lowerCaseFirstLetter = (string) => {
    return `${string?.[0]?.toLowerCase()}${string?.slice(1)}`;
};

export const upperCaseFirstLetter = (string) => {
    return `${string?.[0]?.toUpperCase()}${string?.slice(1)}`;
};

// cspell:ignore parkpass, lowerbound, upsell, activitypass, googlepass
